import dayjs from 'dayjs';
import parse from 'html-react-parser';
import Image from 'next/legacy/image';
import Link from 'next/link';

import { formatListingDate } from '@components/article/ListingCard';
import { DEFAULT_BLOG_IMAGE } from '@lib/constants';
import type { CardLabel } from '@lib/homepage/tabs';
import type { ListingItem } from '@lib/listings/types';

const FALLBACK_AVATAR = '/images/hub-logo.svg';
const FALLBACK_EYEBROW = 'Fantasy Football';

type FeaturedArticleProps = {
  nextGameweek: number | null;
  /** The lead story; nothing renders without one. */
  item?: ListingItem | null;
  label?: CardLabel;
};

/**
 * The dark hero above the article grid: the lead story's strand and the
 * coming gameweek, its title, standfirst and byline beside its image.
 */
const FeaturedArticle = ({
  nextGameweek,
  item,
  label
}: FeaturedArticleProps) => {
  if (!item) return null;
  const eyebrow = label?.text ?? item.categoryName ?? FALLBACK_EYEBROW;

  return (
    <section
      aria-label="Featured article"
      className="bg-neutral-800 pt-8 pb-10"
    >
      <div className="mx-auto max-w-7xl px-4 lg:grid lg:grid-cols-12 lg:items-center lg:gap-8 lg:px-0">
        <div className="lg:col-span-6 lg:px-6">
          <div className="inline-flex items-center gap-[10px] font-open-sans text-[13px] font-semibold uppercase tracking-[.06em] text-white">
            {nextGameweek && (
              <span className="rounded-[6px] bg-primary-600 px-2 py-1">{`Gameweek ${nextGameweek}`}</span>
            )}
            <span className="text-neutral-400">{eyebrow}</span>
          </div>
          <h1 className="mt-5 font-sans text-2xl font-bold leading-[1.1] tracking-[-.01em] text-white [text-wrap:pretty] sm:text-3xl lg:text-[36px]">
            <Link
              href={item.href}
              className="block"
              data-cy="featured-article-link"
            >
              {parse(item.title)}
            </Link>
          </h1>
          {item.standfirst && (
            <p className="mt-4 hidden max-w-[520px] font-open-sans text-[17px] leading-[27px] text-neutral-300 md:block">
              {item.standfirst}
            </p>
          )}
          <div className="mt-5 flex items-center gap-[10px] font-open-sans text-[13px]">
            <img
              className="h-7 w-7 rounded-full bg-neutral-700 object-cover"
              src={item.author?.avatarUrl ?? FALLBACK_AVATAR}
              alt=""
            />
            {item.author && (
              <span className="font-semibold text-white">
                {item.author.name}
              </span>
            )}
            <span className="text-neutral-500">
              {item.publishedAt && (
                <time dateTime={dayjs(item.publishedAt).format('YYYY-MM-DD')}>
                  {formatListingDate(item.publishedAt)}
                </time>
              )}
              {item.publishedAt && item.readMinutes && (
                <span aria-hidden="true"> &middot; </span>
              )}
              {item.readMinutes && <span>{`${item.readMinutes} min`}</span>}
            </span>
          </div>
        </div>
        <div className="mt-6 lg:col-span-6 lg:mt-0 lg:p-1">
          <Link
            href={item.href}
            tabIndex={-1}
            aria-hidden="true"
            className="block"
          >
            <div className="relative aspect-[16/10] overflow-hidden rounded-[12px]">
              <Image
                src={item.image?.url ?? DEFAULT_BLOG_IMAGE}
                alt={item.image?.alt ?? ''}
                layout="fill"
                objectFit="cover"
                priority
                // WP Engine 403s Vercel's optimiser on cache-miss (FFH-1688),
                // and Sanity URLs are already CDN transforms.
                unoptimized
              />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedArticle;
